import 'twin.macro';
import React, { useState, useEffect, useRef, RefObject } from 'react';
import { useAIGenerationContext } from 'components/Editor/AIAssist/contexts/AIGenerationContext';
import { SelectionToolbarButtons as SelectionToolbarUI } from 'components/Editor/AIAssist/components/SelectionToolbar/SelectionToolbarButtons';
import FadeInComponent from 'components/Transitions/FadeIn';
import { ImprovementType } from 'components/Editor/AIAssist/types';
import { SelectionAIResult } from './SelectionAIResult';
import { SelectionPdfResult } from './SelectionPdfResult';
import { validateApplyConditions } from './utils/validateApplyConditions';
import { replaceTextInRange } from './utils/replaceTextInRange';
import { FroalaEditorInstance } from 'components/Editor/froalaTypes';
import { useTextSelection } from './hooks/useTextSelection';
import { useGeneratePdfFromHtml } from '@teetsh/app/src/queries/editor/useGeneratePdfFromHtml';
import { SelectionData } from './utils/captureSelectionData';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
} from '@floating-ui/react';

function getVirtualElement(range: Range) {
  return {
    getBoundingClientRect: () => {
      try {
        return range.getBoundingClientRect();
      } catch {
        return {
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          width: 0,
          height: 0,
          x: 0,
          y: 0,
        } as DOMRect;
      }
    },
  };
}

interface Props {
  visible?: boolean;
  containerRef: RefObject<HTMLDivElement>;
  froalaInstanceRef: RefObject<FroalaEditorInstance | null>;
  onChange: (value: string) => void;
  onPdfAddAttachment?: (file: File) => Promise<void>;
}

type PanelType = 'buttons' | 'ai-result' | 'pdf-result';

export function SelectionToolbar({
  visible = true,
  containerRef,
  froalaInstanceRef,
  onChange,
  onPdfAddAttachment,
}: Props) {
  const { generate, isLoading, currentAction, data, hasData, reset } =
    useAIGenerationContext();

  const [showToolbar, setShowToolbar] = useState(true);
  const [activePanel, setActivePanel] = useState<PanelType>('buttons');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const displayRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Track when user explicitly closes toolbar to prevent auto-reopening
  // for the same selection. This allows users to dismiss the toolbar
  // while keeping their text selection active.
  const closedSelectionRef = useRef<SelectionData | null>(null);

  const { selection, hasSelection, clearSelection } = useTextSelection(
    containerRef,
    {
      enabled: true,
      locked: activePanel === 'pdf-result' || isMenuOpen,
    },
  );

  const pdfGeneration = useGeneratePdfFromHtml();

  const { refs, floatingStyles } = useFloating({
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (hasData && currentAction && currentAction !== 'custom') {
      setActivePanel('ai-result');
    }
  }, [hasData, currentAction]);

  useEffect(() => {
    // Auto-show toolbar when new selection is made
    // BUT don't show if user explicitly closed it for this selection
    if (selection && selection !== closedSelectionRef.current) {
      setShowToolbar(true);
      closedSelectionRef.current = null; // Reset closed state on new selection
      return;
    }

    if (activePanel === 'pdf-result') {
      return;
    }

    if (currentAction && currentAction !== 'custom') {
      reset();
    }
    setShowToolbar(false);
  }, [selection, reset, currentAction, activePanel]);

  useEffect(() => {
    if (selection?.range) {
      const virtualEl = getVirtualElement(selection.range);
      refs.setReference(virtualEl as any);
    }
  }, [selection?.range, refs]);

  useEffect(() => {
    if (!showToolbar) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;

      // Close the SelectionToolbarButtons when clicking on Froala editor toolbar
      // This hides our AI toolbar UI but preserves the text selection itself
      // so the user can still use Froala's formatting buttons on the selected text
      if (
        target.closest('.fr-toolbar') ||
        target.closest('.fr-second-toolbar') ||
        target.closest('.fr-more-toolbar') ||
        target.closest('.fr-popup')
      ) {
        setShowToolbar(false);
        setActivePanel('buttons');
        reset();
      }
    }

    // Use capture phase to detect clicks before Froala might stop propagation
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [showToolbar, reset]);

  if (!visible || !hasSelection || !selection || !showToolbar) {
    return null;
  }

  const isPdfGenerating = pdfGeneration.isGenerating;

  async function handleSelect(type: ImprovementType) {
    if (!selection) return;

    if (type === 'generatePdf') {
      setActivePanel('pdf-result');
      await pdfGeneration.generate({ html: selection.html });
      return;
    }

    generate({ text: selection.html, type });
  }

  function handleClose() {
    reset();
    // Remember user closed toolbar for this selection
    // to prevent auto-reopen while selection remains active
    closedSelectionRef.current = selection;
    setShowToolbar(false);
    setActivePanel('buttons');
  }

  function handlePdfDownload() {
    pdfGeneration.download();
    clearSelection();
    setShowToolbar(false);
    setActivePanel('buttons');
  }

  async function handlePdfAddAttachment() {
    if (pdfGeneration.pdfUrl && pdfGeneration.filename && onPdfAddAttachment) {
      const pdfBlob = await fetch(pdfGeneration.pdfUrl).then(res => res.blob());
      const file = new File([pdfBlob], pdfGeneration.filename, {
        type: 'application/pdf',
      });
      await onPdfAddAttachment(file);
    }
    clearSelection();
    setShowToolbar(false);
    setActivePanel('buttons');
  }

  function handlePdfCancel() {
    clearSelection();
    setActivePanel('buttons');
    setShowToolbar(false);
  }

  function handleApply() {
    if (!selection) return;

    const validation = validateApplyConditions(selection, containerRef);
    if (!validation.isValid) {
      reset();
      return;
    }

    const editor = froalaInstanceRef.current;
    if (editor && editor.undo) {
      try {
        replaceTextInRange(selection.range, data);
        editor.undo.saveStep();
        const editorElement =
          containerRef.current?.querySelector('.fr-element');
        if (editorElement) {
          onChange(editorElement.innerHTML);
        }
      } catch (error) {
        // Error applying AI result
      }
    }

    reset();
    clearSelection();
    setShowToolbar(false);
    setActivePanel('buttons');
  }

  function handleIgnore() {
    reset();
    clearSelection();
    setShowToolbar(false);
    setActivePanel('buttons');
  }

  return (
    <FloatingPortal>
      <FadeInComponent delay={0} zIndex="20">
        <div ref={toolbarRef} data-ai-toolbar>
          <div ref={refs.setFloating} style={floatingStyles}>
            {activePanel === 'buttons' && (
              <SelectionToolbarUI
                onSelect={handleSelect}
                onClose={handleClose}
                isLoading={isLoading || isPdfGenerating}
                currentAction={isPdfGenerating ? 'generatePdf' : currentAction}
                onMenuOpenChange={setIsMenuOpen}
              />
            )}
            {activePanel === 'ai-result' && currentAction && (
              <div ref={displayRef}>
                <SelectionAIResult
                  data={data}
                  originalText={selection.html}
                  onApply={handleApply}
                  onIgnore={handleIgnore}
                  isLoading={isLoading}
                  diffMode={currentAction === 'fixGrammar' ? 'chars' : 'words'}
                  action={currentAction}
                />
              </div>
            )}
            {activePanel === 'pdf-result' && (
              <div ref={displayRef}>
                <SelectionPdfResult
                  isLoading={isPdfGenerating}
                  pdfUrl={pdfGeneration.pdfUrl || undefined}
                  onDownload={handlePdfDownload}
                  onAddAttachment={handlePdfAddAttachment}
                  onCancel={handlePdfCancel}
                  hasAttachmentOption={!!onPdfAddAttachment}
                />
              </div>
            )}
          </div>
        </div>
      </FadeInComponent>
    </FloatingPortal>
  );
}
